import type * as PDFjs from "pdfjs-dist/build/pdf";
import type * as PDFjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";
import type {
  PDFDocumentProxy,
  PDFWorker,
} from "pdfjs-dist/types/src/display/api";

import { hmrValue } from "../../../../modules/shared-utils/vite";
import { raise } from "../../../../modules/shared-utils/assertions";

/**
 * The PDF.js modules are injected here by the PDFLoader (see ./loader.ts)
 * so the navigator code can use them without static value imports:
 * a static import would hoist PDF.js out of its lazily-loaded chunks
 * and leave facade chunks behind for the loader's dynamic imports.
 */
const config = hmrValue<{
  lib: typeof PDFjs | null;
  viewer: typeof PDFjsViewer | null;
  workerInstance: PDFWorker | null;
  documentPromise: Promise<PDFDocumentProxy> | null;
}>(
  "PDFjsDependencies",
  () => ({
    lib: null,
    viewer: null,
    workerInstance: null,
    documentPromise: null,
  }),
  import.meta.hot
);

export function setPDFjsDependencies(
  lib: typeof PDFjs,
  viewer: typeof PDFjsViewer,
  worker: PDFWorker,
  document: Promise<PDFDocumentProxy>
) {
  config.lib = lib;
  config.viewer = viewer;
  config.workerInstance = worker;
  config.documentPromise = document;
}

export const pdfjsLib = () => config.lib ?? raise("PDF.js lib not loaded");
export const pdfjsViewer = () =>
  config.viewer ?? raise("PDF.js viewer not loaded");
export const pdfjsWorker = () =>
  config.workerInstance ?? raise("PDF.js worker not initialized");
export const pdfjsDocument = () =>
  config.documentPromise ?? raise("PDF document not requested");
